// NEW_FILE_JUSTIFIED: Loading skeleton placeholders for messaging UI — no existing skeleton components exist for conversation list, chat panel, or contact panel

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/** Skeleton for a single conversation row in the sidebar */
function ConversationItemSkeleton() {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <Skeleton className="size-10 shrink-0 rounded-full" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1.5">
          <Skeleton className="h-3.5 w-28" />
          <Skeleton className="h-3 w-10" />
        </div>
        <Skeleton className="h-3 w-40" />
      </div>
    </div>
  );
}

/** Skeleton for the conversation list sidebar */
export function ConversationListSkeleton() {
  return (
    <nav
      aria-label="Loading conversations"
      aria-busy="true"
      className="w-100 flex flex-col h-full border-r border-msg-panel-border bg-msg-panel-bg shrink-0 overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <Skeleton className="h-5 w-36" />
        <Skeleton className="size-9 rounded-lg" />
      </div>

      {/* Search bar */}
      <div className="px-4">
        <Skeleton className="h-9 w-full rounded-lg" />
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-1 px-4 pt-3">
        {['w-12', 'w-16', 'w-14', 'w-14'].map((w, i) => (
          <Skeleton key={i} className={cn('h-8 flex-1 rounded-lg', w)} />
        ))}
      </div>

      {/* Divider */}
      <div className="h-px w-full bg-msg-panel-border mt-3" />

      {/* Conversation rows */}
      <div className="flex-1 overflow-hidden">
        {Array.from({ length: 7 }, (_, i) => (
          <ConversationItemSkeleton key={i} />
        ))}
      </div>
    </nav>
  );
}

/** Skeleton for a single message bubble */
function MessageBubbleSkeleton({ isOutgoing, width }: { isOutgoing: boolean; width: string }) {
  return (
    <div className={cn('flex', isOutgoing ? 'justify-end' : 'items-end gap-2')}>
      <div className={cn('max-w-[75%]', isOutgoing ? 'w-fit ml-auto' : 'w-fit')}>
        <div
          className={cn(
            'rounded-2xl px-4 py-3',
            isOutgoing
              ? 'bg-msg-bubble-outgoing/50 rounded-br-sm'
              : 'bg-msg-bubble-incoming/50 rounded-bl-sm'
          )}
        >
          <Skeleton className={cn('h-3.5', width)} />
          <Skeleton className="h-3.5 w-24 mt-1.5" />
        </div>
        <Skeleton className={cn('h-2.5 w-8 mt-1', isOutgoing ? 'ml-auto' : '')} />
      </div>
    </div>
  );
}

/** Skeleton for the chat panel (header + messages + composer) */
export function ChatPanelSkeleton() {
  return (
    <div
      aria-label="Loading messages"
      aria-busy="true"
      className="flex flex-1 flex-col min-w-0 bg-msg-panel-bg"
    >
      {/* Chat Header */}
      <div className="flex h-16 items-center justify-between px-5 border-b border-msg-panel-border">
        <div className="flex items-center gap-3">
          <Skeleton className="size-10 rounded-full" />
          <div className="flex flex-col gap-1.5">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-16" />
          </div>
        </div>
        <div className="flex gap-2">
          <Skeleton className="size-9 rounded-lg" />
          <Skeleton className="size-9 rounded-lg" />
          <Skeleton className="size-9 rounded-lg" />
        </div>
      </div>

      {/* Messages skeleton */}
      <div className="flex-1 overflow-hidden">
        <div className="flex flex-col gap-4 p-5">
          <MessageBubbleSkeleton isOutgoing={false} width="w-48" />
          <MessageBubbleSkeleton isOutgoing={true} width="w-56" />
          <MessageBubbleSkeleton isOutgoing={false} width="w-64" />
          <MessageBubbleSkeleton isOutgoing={true} width="w-40" />
          <MessageBubbleSkeleton isOutgoing={false} width="w-52" />
        </div>
      </div>

      {/* Composer skeleton */}
      <div className="border-t border-msg-panel-border p-4">
        <Skeleton className="h-20 w-full rounded-xl" />
      </div>
    </div>
  );
}

/** Skeleton for the right-side contact panel */
export function ContactPanelSkeleton() {
  return (
    <div
      aria-label="Loading contact details"
      aria-busy="true"
      className="flex h-full w-80 shrink-0 flex-col border-l border-msg-panel-border bg-msg-panel-bg"
    >
      <div className="flex flex-col gap-5 p-5">
        {/* Avatar + name */}
        <div className="flex flex-col items-center gap-3">
          <Skeleton className="size-[72px] rounded-full" />
          <Skeleton className="h-5 w-36" />
          <Skeleton className="h-6 w-20 rounded-full" />
          <div className="flex gap-2">
            <Skeleton className="size-9 rounded-lg" />
            <Skeleton className="size-9 rounded-lg" />
          </div>
        </div>

        <Skeleton className="h-px w-full" />

        {/* Contact info */}
        <div className="flex flex-col gap-3">
          <Skeleton className="h-4 w-16" />
          <div className="flex flex-col gap-2.5">
            <div className="flex items-center gap-3">
              <Skeleton className="size-4 rounded" />
              <Skeleton className="h-3.5 w-28" />
            </div>
            <div className="flex items-center gap-3">
              <Skeleton className="size-4 rounded" />
              <Skeleton className="h-3.5 w-32" />
            </div>
            <div className="flex items-center gap-3">
              <Skeleton className="size-4 rounded" />
              <Skeleton className="h-3.5 w-24" />
            </div>
          </div>
        </div>

        <Skeleton className="h-px w-full" />

        {/* Tags */}
        <div className="flex flex-col gap-3">
          <Skeleton className="h-4 w-10" />
          <div className="flex gap-2">
            <Skeleton className="h-6 w-16 rounded-full" />
            <Skeleton className="h-6 w-20 rounded-full" />
            <Skeleton className="h-6 w-14 rounded-full" />
          </div>
        </div>

        <Skeleton className="h-px w-full" />

        {/* Notes */}
        <div className="flex flex-col gap-3">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-20 w-full rounded-xl" />
          <Skeleton className="h-16 w-full rounded-xl" />
        </div>
      </div>
    </div>
  );
}

/** Skeleton for Gmail email list rows */
export function EmailListSkeleton() {
  return (
    <div aria-label="Loading emails" aria-busy="true" className="flex flex-col">
      {/* Tabs */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
        <Skeleton className="h-8 w-20 rounded-lg" />
        <Skeleton className="h-8 w-16 rounded-lg" />
      </div>

      {/* Search */}
      <div className="px-4 py-3">
        <Skeleton className="h-9 w-full rounded-lg" />
      </div>

      {/* Email rows */}
      {Array.from({ length: 8 }, (_, i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-3 border-b border-border">
          <Skeleton className="size-4 rounded" />
          <Skeleton className="h-4 w-32 shrink-0" />
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="h-3 w-12 shrink-0" />
        </div>
      ))}
    </div>
  );
}
